// SCR-015: the installer session console. The transcript, proposal, changes and verification
// checklist all come from one SSE connection (API-040 `streamInstallerEvents`); this component only
// folds events into state (`applyStreamEvent`) and derives what to show (`model.ts`).
import { useContext, useEffect, useState } from "preact/hooks";
import type { JSX } from "preact";
import type { ScreenProps } from "../../router/route";
import { RouterContext } from "../../router/router";
import { useApi } from "@nexus/ui";
import { ApiError } from "@nexus/ui";
import { SessionContext } from "../../session/session";
import { ErrorBanner, Loading } from "@nexus/ui";
import { openEventStream, parseInstallerEvent, type StreamStatus } from "../../ui/sse";
import { Link, useBreadcrumb } from "../../ui/shell";
import { Dialog } from "@nexus/ui";
import { Field, InputAffix, TitleWithHelp, errorId } from "@nexus/ui";
import { useCustomerName } from "../../ui/customer-directory";
import { actorDisplay, actorTitle, knownActor } from "../../ui/identity";
import { Card, Chip, CopyId, PageHeader } from "@nexus/ui";
import { signInPath } from "../signin/model";
import { InstallerDisabled } from "./disabled";
import {
  applyStreamEvent,
  budgetMeterView,
  changeKindLabel,
  changeStatusLabel,
  changeStatusTone,
  emptyConsoleState,
  emptyExtendBudgetDraft,
  extendBudgetRequest,
  installerChangePath,
  isInstallerDisabled,
  pendingChanges,
  sessionActionErrorMessage,
  sessionHeaderView,
  sessionStateTone,
  streamEventEndsSession,
  transcriptRow,
  validateExtendBudget,
  verificationChecklist,
  type ConsoleState,
  type ExtendBudgetDraft,
  type InstallerChange,
  type InstallerSessionState,
} from "./model";
import { CredentialsPanel } from "./credentials-panel";
import { Proposal } from "./proposal";

const STREAM_STATUS_LABELS: Record<StreamStatus, string | null> = {
  connecting: "Connecting…",
  open: null,
  reconnecting: "Connection lost — reconnecting…",
  closed: null,
};

function clock(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

function when(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

export function Console({ params }: ScreenProps<"/installer/sessions/:sessionId">) {
  const { sessionId } = params;
  const api = useApi();
  const router = useContext(RouterContext);
  const services = useContext(SessionContext);
  const signedIn = services?.state.status === "signed-in" ? services.state.session : null;
  const canApprove = !!signedIn && signedIn.can("installer:approve");

  const [state, setState] = useState<ConsoleState>(emptyConsoleState);
  const [streamStatus, setStreamStatus] = useState<StreamStatus>("connecting");
  const [streamError, setStreamError] = useState<ApiError | null>(null);
  const [now, setNow] = useState(() => new Date());
  const [composerText, setComposerText] = useState("");
  const [actionError, setActionError] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);
  const [extending, setExtending] = useState(false);

  const session = state.session;
  const customerName = useCustomerName(session?.orgId ?? null);
  useBreadcrumb(session ? [{ label: `${customerName} · ${session.awsAccountId}` }] : null);

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    setState(emptyConsoleState);
    setStreamError(null);
    const stream = openEventStream({
      url: `/v1/installer/sessions/${encodeURIComponent(sessionId)}/events`,
      fetch: (input, init) => fetch(input, init),
      onEvent: (event) => {
        const parsed = parseInstallerEvent(event);
        if (!parsed) return;
        setState((current) => applyStreamEvent(current, parsed));
        // A session that reached done/failed/cancelled will send no further events; closing here
        // stops the browser from reconnecting a stream nothing more will ever arrive on (every
        // reconnect otherwise counts as session activity, TASK-140 follow-up).
        if (streamEventEndsSession(parsed)) stream.close();
      },
      onStatus: setStreamStatus,
      onError: (error) => {
        setStreamError(error);
        // `openEventStream` already stops retrying a non-retryable failure (401/403 included) —
        // closed here too, explicitly, rather than relying on that alone.
        stream.close();
      },
      onSessionExpired: () => {
        stream.close();
        if (router) router.navigate(signInPath("expired", router.location().pathname), { replace: true });
      },
    });
    return () => stream.close();
  }, [sessionId, router]);

  async function postMessage(event: JSX.TargetedEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!api || composerText.trim() === "") return;
    setBusy(true);
    setActionError(null);
    try {
      await api.post("/v1/installer/sessions/{sessionId}/messages", { path: { sessionId }, body: { text: composerText.trim() } });
      setComposerText("");
    } catch (e) {
      if (e instanceof ApiError) setActionError(e);
      else throw e;
    } finally {
      setBusy(false);
    }
  }

  async function cancelSession() {
    if (!api) return;
    setBusy(true);
    setActionError(null);
    try {
      await api.post("/v1/installer/sessions/{sessionId}/cancel", { path: { sessionId } });
    } catch (e) {
      if (e instanceof ApiError) setActionError(e);
      else throw e;
    } finally {
      setBusy(false);
    }
  }

  if (!api) return <p role="alert">Admin API not available.</p>;
  if (isInstallerDisabled(streamError)) return <InstallerDisabled />;

  const { messages, changes } = state;
  const streamBanner = STREAM_STATUS_LABELS[streamStatus];
  const view = session ? sessionHeaderView(session, now, canApprove) : null;
  const pendingCount = pendingChanges(changes).length;

  return (
    <div class="page installer-console">
      {session && view ? (
        <PageHeader
          title={`${customerName} — ${session.awsAccountId}`}
          titleExtra={<Chip tone={sessionStateTone(session.state)}>{view.stateLabel}</Chip>}
          meta={
            <>
              <CopyId id={sessionId} label="Session" what="session id" />
              <span title={actorTitle(session.createdBy, knownActor(session, "createdBy"))}>
                Started by {actorDisplay(session.createdBy, signedIn?.me, knownActor(session, "createdBy"))} · {when(session.createdAt)}
              </span>
            </>
          }
          actions={
            view.canCancel && (
              <button type="button" class="button button-danger" disabled={busy} onClick={() => void cancelSession()}>
                Cancel session
              </button>
            )
          }
        />
      ) : (
        <PageHeader title="Installer session" />
      )}
      {streamError && <ErrorBanner error={streamError} />}
      {streamBanner && (
        <p class="banner banner-info" role="status">
          {streamBanner}
        </p>
      )}
      {actionError && (
        <p role="alert" class="banner banner-error">
          {sessionActionErrorMessage(actionError)}
        </p>
      )}
      {!session || !view ? (
        !streamError && <Loading label="Opening the session…" />
      ) : (
        <>
          <section class="card console-facts" aria-label="Session details">
            <dl class="console-header-facts">
              <div>
                <dt>Status</dt>
                <dd>{view.stateLabel}</dd>
              </div>
              <div>
                <dt>Goal</dt>
                <dd>{view.goalLabel}</dd>
              </div>
              <div>
                <dt>Access</dt>
                <dd>{view.accessModeLabel}</dd>
              </div>
              <div>
                <dt>Regions</dt>
                <dd class="mono">{view.regions}</dd>
              </div>
              <div>
                <dt>Pending changes</dt>
                <dd>{pendingCount}</dd>
              </div>
            </dl>
          </section>
          <div class="split console-layout">
            <section class="card transcript" aria-labelledby="transcript-heading">
              <h2 id="transcript-heading">Transcript</h2>
              {messages.length === 0 ? (
                <p class="muted">Nothing yet. The agent's messages and tool calls appear here as they happen.</p>
              ) : (
                // Scrolls once a session has a few turns, so it takes focus (axe
                // scrollable-region-focusable; the a11y walk found it once FLOW-008 ran through).
                <ol class="transcript-rows" tabIndex={0} aria-labelledby="transcript-heading">
                  {messages.map((message) => {
                    const row = transcriptRow(message);
                    return (
                      <li key={row.seq} class={`transcript-row transcript-${row.kind}`}>
                        <div class="transcript-meta">
                          <span class="transcript-actor">{row.actorLabel}</span>
                          <time class="transcript-time" dateTime={row.createdAt}>
                            {clock(row.createdAt)}
                          </time>
                        </div>
                        {row.tool ? (
                          <details class="transcript-tool">
                            <summary>
                              <code>{row.tool.name}</code> — {row.tool.durationText}
                              {row.tool.outcomeLabel ? ` — ${row.tool.outcomeLabel}` : ""}
                            </summary>
                            <pre class="transcript-tool-detail">{row.tool.detail}</pre>
                          </details>
                        ) : (
                          <p class="transcript-text">{row.text}</p>
                        )}
                      </li>
                    );
                  })}
                </ol>
              )}
              {view.canCompose && (
                <form class="composer stack-sm" onSubmit={(event) => void postMessage(event)}>
                  <Field id="composer-text" label="Instruction to the agent" help="installer.instruction">
                    <textarea
                      id="composer-text"
                      maxLength={4000}
                      placeholder="For example: use the existing VPC vpc-0abc, internal load balancer only."
                      value={composerText}
                      onInput={(event) => setComposerText(event.currentTarget.value)}
                    />
                  </Field>
                  <div class="form-actions">
                    <button type="submit" class="button button-primary" disabled={busy || composerText.trim() === ""}>
                      Send
                    </button>
                  </div>
                </form>
              )}
            </section>
            <div class="stack console-side">
              {session.state === "awaiting_credentials" && (
                <CredentialsPanel sessionId={sessionId} accessMode={session.accessMode} onSubmitted={() => undefined} />
              )}
              <BudgetCard state={state} now={now} canExtend={view.canExtend} busy={busy} onExtend={() => setExtending(true)} />
              <Proposal messages={messages} />
              <ChangesList sessionId={sessionId} changes={changes} />
              <Verification state={session.state} />
            </div>
          </div>
          {extending && (
            <ExtendBudgetDialog sessionId={sessionId} onClose={() => setExtending(false)} onExtended={() => setExtending(false)} />
          )}
        </>
      )}
    </div>
  );
}

function BudgetCard({
  state,
  now,
  canExtend,
  busy,
  onExtend,
}: {
  state: ConsoleState;
  now: Date;
  canExtend: boolean;
  busy: boolean;
  onExtend(): void;
}) {
  const session = state.session;
  if (!session) return null;
  // A live `budget` stream event is fresher than the session snapshot it arrived after.
  const meter = budgetMeterView(state.budget ?? session, now);
  return (
    <Card
      title="Budget"
      help="installer.budget"
      class="budget-card"
      actions={
        canExtend && (
          <button type="button" class="button button-sm" disabled={busy} onClick={onExtend}>
            Extend budget
          </button>
        )
      }
    >
      <p class="budget-figures">
        <span class="mono">{meter.spentText}</span> <span class="muted">of</span> <span class="mono">{meter.limitText}</span>
        {meter.exhausted && (
          <>
            {" "}
            <Chip tone="amber">Used up</Chip>
          </>
        )}
      </p>
      <svg class="budget-meter" viewBox="0 0 100 8" preserveAspectRatio="none" role="img" aria-label={`${meter.percent}% of the budget spent`}>
        <rect class="budget-meter-track" x="0" y="0" width="100" height="8" rx="4" />
        {meter.percent > 0 && (
          <rect class={meter.exhausted ? "budget-meter-fill budget-meter-full" : "budget-meter-fill"} x="0" y="0" width={Math.max(2, meter.percent)} height="8" rx="4" />
        )}
      </svg>
      <p class="muted">
        {meter.turnsText} turns · {meter.timeLeftText === "Time's up" ? "Time's up" : `${meter.timeLeftText} left`}
      </p>
    </Card>
  );
}

function ChangesList({ sessionId, changes }: { sessionId: string; changes: readonly InstallerChange[] }) {
  return (
    <Card title="Changes" help="installer.changes" class="changes-list">
      {changes.length === 0 ? (
        <p class="muted">No changes proposed yet.</p>
      ) : (
        <ul class="changes-rows">
          {changes.map((change) => (
            <li key={change.id}>
              <Link href={installerChangePath(sessionId, change.id)}>{changeKindLabel(change.kind)}</Link>
              <Chip tone={changeStatusTone(change.status)}>{changeStatusLabel(change.status)}</Chip>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function Verification({ state }: { state: InstallerSessionState }) {
  const steps = verificationChecklist(state);
  return (
    <section class="card verification-checklist" aria-labelledby="verification-heading">
      <TitleWithHelp id="verification-heading" title="Verification" help="installer.verification" />
      <ol class="verification-steps">
        {steps.map((step) => (
          <li key={step.key} class={step.done ? "verification-done" : undefined}>
            <span class="verification-mark" aria-hidden="true">
              {step.done ? "✓" : ""}
            </span>
            <span>{step.label}</span>
            <span class="visually-hidden">{step.done ? " — done" : " — not yet"}</span>
          </li>
        ))}
      </ol>
    </section>
  );
}

function ExtendBudgetDialog({ sessionId, onClose, onExtended }: { sessionId: string; onClose(): void; onExtended(): void }) {
  const api = useApi();
  const [draft, setDraft] = useState<ExtendBudgetDraft>(emptyExtendBudgetDraft);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const errors = validateExtendBudget(draft);
  const touched = draft.addUsd !== "" || draft.addTurns !== "" || draft.addMinutes !== "";

  async function submit(event: JSX.TargetedEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!api || Object.keys(errors).length > 0) return;
    setBusy(true);
    setError(null);
    try {
      await api.post("/v1/installer/sessions/{sessionId}/budget", { path: { sessionId }, body: extendBudgetRequest(draft) });
      onExtended();
    } catch (e) {
      if (e instanceof ApiError) setError(e);
      else throw e;
    } finally {
      setBusy(false);
    }
  }

  const shown = (key: keyof ExtendBudgetDraft) => (touched ? errors[key] : undefined);

  return (
    <Dialog
      title="Extend budget"
      open
      onClose={onClose}
      actions={
        <>
          <button type="button" class="button button-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form="extend-budget-form" class="button button-primary" disabled={busy || Object.keys(errors).length > 0}>
            {busy ? "Extending…" : "Extend"}
          </button>
        </>
      }
    >
      <form id="extend-budget-form" class="stack" onSubmit={(event) => void submit(event)}>
        <p class="muted">Add to what is left. Each field may stay empty; the extension is audited.</p>
        {error && (
          <p role="alert" class="banner banner-error">
            {sessionActionErrorMessage(error)}
          </p>
        )}
        <Field id="extend-usd" label="Add dollars" help="installer.extendUsd" error={shown("addUsd")} class="field-narrow">
          <InputAffix prefix="$">
            <input
              id="extend-usd"
              type="text"
              inputMode="decimal"
              value={draft.addUsd}
              aria-invalid={shown("addUsd") ? "true" : undefined}
              aria-describedby={shown("addUsd") ? errorId("extend-usd") : undefined}
              onInput={(e) => setDraft({ ...draft, addUsd: e.currentTarget.value })}
            />
          </InputAffix>
        </Field>
        <Field id="extend-turns" label="Add turns" help="installer.extendTurns" error={shown("addTurns")} class="field-narrow">
          <input
            id="extend-turns"
            type="text"
            inputMode="numeric"
            value={draft.addTurns}
            aria-invalid={shown("addTurns") ? "true" : undefined}
            aria-describedby={shown("addTurns") ? errorId("extend-turns") : undefined}
            onInput={(e) => setDraft({ ...draft, addTurns: e.currentTarget.value })}
          />
        </Field>
        <Field id="extend-minutes" label="Add minutes" help="installer.extendMinutes" error={shown("addMinutes")} class="field-narrow">
          <input
            id="extend-minutes"
            type="text"
            inputMode="numeric"
            value={draft.addMinutes}
            aria-invalid={shown("addMinutes") ? "true" : undefined}
            aria-describedby={shown("addMinutes") ? errorId("extend-minutes") : undefined}
            onInput={(e) => setDraft({ ...draft, addMinutes: e.currentTarget.value })}
          />
        </Field>
      </form>
    </Dialog>
  );
}

export { Console as ConsoleScreen };
